'use client';

import { useId } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { cabinetSvg } from '../drawing/cabinet';
import { doorSvg } from '../drawing/door';
import { SvgArt } from '../drawing/SvgArt';
import { safeHex } from '../drawing/svg';
import {
  childLevel,
  headingLevel,
  items,
  SkLink,
  text,
  type HeadingLevel,
  type Localized,
} from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';
import { dimension, doorStyleOr, homeSurfaceClass, HomeSurfaceStyles, type HomeSurface } from './homeShared';

/**
 * "Ways to buy": two to four cards, each one link to a journey (replacement
 * doors, cabinets, a complete kitchen, bedrooms).
 *
 * <p>The whole card is a single `<a>`, so a keyboard user tabs once per
 * journey. The drawing is decorative (`aria-hidden`): the card's title names
 * the link. The `from` line is a price the owner writes; it has no default.
 *
 * <p>Four columns from 1101 px, two from 601 px, one below.
 */
export type JourneyArtKind = 'door' | 'cabinet' | 'plan';

export interface SpatialKitchenJourneyArt {
  kind?: JourneyArtKind;
  style?: string;
  colour?: string;
  /** Drawing width and height in mm. */
  w?: number;
  h?: number;
}

export interface SpatialKitchenJourney {
  art?: SpatialKitchenJourneyArt;
  title?: Localized;
  body?: Localized;
  /** A from-price line, written out: "From £18 per door". */
  from?: Localized;
  ctaLabel?: Localized;
  href?: string;
}

export interface SpatialKitchenJourneysProps {
  eyebrow?: Localized;
  title?: Localized;
  intro?: Localized;
  journeys?: SpatialKitchenJourney[];
  surface?: HomeSurface;
  headingLevel?: HeadingLevel;
}

const CSS = `
.sk-journeys__grid{display:grid;gap:18px;margin:0;padding:0;list-style:none}
@media (min-width:601px){.sk-journeys__grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media (min-width:1101px){.sk-journeys__grid--3{grid-template-columns:repeat(3,minmax(0,1fr))}.sk-journeys__grid--4{grid-template-columns:repeat(4,minmax(0,1fr))}}
.sk-journeys__grid > li{display:flex;min-width:0}
.sk-journey{display:flex;flex-direction:column;gap:14px;width:100%;min-height:320px;padding:26px;background:var(--sk-white);border:1px solid var(--sk-border);border-radius:var(--sk-radius-card);color:var(--sk-ink);text-decoration:none;transition:box-shadow .2s,transform .2s,border-color .2s}
.sk-journey:hover{box-shadow:var(--sk-shadow-lg);transform:translateY(-3px);border-color:transparent}
@media (prefers-reduced-motion:reduce){.sk-journey:hover{transform:none}}
.sk-journey__art{display:grid;place-items:center;height:120px;border-radius:10px;background:var(--sk-stone);overflow:hidden}
.sk-journey__art .sk-art{display:block;height:100px}
.sk-journey__art svg{display:block;height:100%;width:auto}
.sk-journey__title{font-size:1.3rem}
.sk-journey__body{flex:1;margin:0;font-size:.94rem;color:var(--sk-muted)}
.sk-journey__from{font-size:.85rem;color:var(--sk-faint)}
`;

/** The mockup's small top-down room plan: an L of units and an island, in the given unit colour. */
function planSvg(colour: string): string {
  const hex = safeHex(colour);
  return (
    '<svg viewBox="0 0 120 90" xmlns="http://www.w3.org/2000/svg">' +
    '<rect x="10" y="10" width="100" height="70" rx="3" fill="#fff" stroke="#d3c7b6"/>' +
    `<rect x="10" y="10" width="100" height="16" fill="${hex}"/>` +
    `<rect x="10" y="26" width="16" height="54" fill="${hex}"/>` +
    '<rect x="48" y="44" width="40" height="18" rx="2" fill="#c49a6c"/>' +
    '</svg>'
  );
}

function journeyArtSvg(art: SpatialKitchenJourneyArt | undefined, idPrefix: string): string {
  const kind: JourneyArtKind = art?.kind === 'cabinet' || art?.kind === 'plan' ? art.kind : 'door';
  const colour = safeHex(art?.colour);
  if (kind === 'plan') return planSvg(colour);
  const style = doorStyleOr(art?.style);
  if (kind === 'cabinet') {
    return cabinetSvg({
      type: 'base',
      w: dimension(art?.w, 500),
      h: dimension(art?.h, 720),
      style,
      colour,
      idPrefix,
    });
  }
  return doorSvg(style, colour, { w: dimension(art?.w, 300), h: dimension(art?.h, 480), idPrefix });
}

function JourneyCard({ journey, TitleTag }: { journey: SpatialKitchenJourney; TitleTag: 'h2' | 'h3' | 'h4' }) {
  const artId = useId();
  const body = text(journey.body);
  const from = text(journey.from);
  const ctaLabel = text(journey.ctaLabel);
  return (
    <SkLink href={journey.href} className="sk-journey">
      <span className="sk-journey__art" aria-hidden="true">
        <SvgArt svg={journeyArtSvg(journey.art, artId)} />
      </span>
      <TitleTag className="sk-heading sk-journey__title">{text(journey.title)}</TitleTag>
      {body && <p className="sk-journey__body">{body}</p>}
      {from && <span className="sk-journey__from">{from}</span>}
      {ctaLabel && <span className="sk-link-arrow">{ctaLabel}</span>}
    </SkLink>
  );
}

export function SpatialKitchenJourneys(props: SpatialKitchenJourneysProps) {
  const journeys = items<SpatialKitchenJourney>(props.journeys)
    .filter((journey) => text(journey.title))
    .slice(0, 4);
  const level = headingLevel(props.headingLevel, 'h2');
  const Title = level;
  const CardTitle = childLevel(level);
  const titleId = useId();
  const columns = journeys.length >= 4 ? 4 : journeys.length === 3 ? 3 : 2;

  return (
    <BlockRoot
      as="section"
      className={`sk sk-journeys sk-section ${homeSurfaceClass(props.surface, 'warm')}`}
      aria-labelledby={titleId}
    >
      <SkBaseStyles />
      <HomeSurfaceStyles />
      <SkStyles id="journeys" css={CSS} />
      <div className="sk-container">
        <div className="sk-section-head">
          {text(props.eyebrow) && <p className="sk-eyebrow">{text(props.eyebrow)}</p>}
          <Title id={titleId} className="sk-heading sk-h2">
            {text(props.title, 'Ways to shop')}
          </Title>
          {text(props.intro) && <p className="sk-lead">{text(props.intro)}</p>}
        </div>
        <ul className={`sk-journeys__grid sk-journeys__grid--${columns}`}>
          {journeys.map((journey, index) => (
            <li key={`${text(journey.title)}-${index}`}>
              <JourneyCard journey={journey} TitleTag={CardTitle} />
            </li>
          ))}
        </ul>
      </div>
    </BlockRoot>
  );
}
